import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:7001/api';

interface User {
  id: string;
  email: string;
  displayName: string;
  tenantId: string;
  role: string;
  isTenantAdmin: boolean;
  mfaEnabled: boolean;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  requiresMfa: boolean;
  mfaToken: string | null;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  verifyMfa: (code: string) => Promise<void>;
  logout: () => void;
  ensureValidToken: () => Promise<string>;
  /**
   * Apply a token response from a non-password auth flow (passkey, magic link).
   * Updates the in-memory auth state synchronously so the SPA re-renders into
   * the authenticated tree without needing a full page reload — important on
   * the OAuth authorize path where the URL query string must survive.
   */
  applyTokens: (data: any) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

function parseJwt(token: string): Record<string, any> | null {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const payload = parseJwt(token);
  if (!payload?.exp) return true;
  return Date.now() >= payload.exp * 1000 - 60000; // 1 min buffer
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const stored = sessionStorage.getItem('auth');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.accessToken && !isTokenExpired(parsed.accessToken)) {
          return { ...parsed, requiresMfa: false, mfaToken: null };
        }
      } catch { /* ignore */ }
    }
    return {
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      requiresMfa: false,
      mfaToken: null,
    };
  });

  useEffect(() => {
    if (state.isAuthenticated) {
      sessionStorage.setItem('auth', JSON.stringify({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: true,
      }));
    } else {
      sessionStorage.removeItem('auth');
    }
  }, [state.isAuthenticated, state.accessToken, state.refreshToken, state.user]);

  const applyTokenResponse = useCallback((data: any) => {
    setState({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      user: {
        id: data.userId || '',
        email: '',
        displayName: data.displayName || '',
        tenantId: data.tenantId || '',
        role: data.role || '',
        isTenantAdmin: data.isTenantAdmin || false,
        mfaEnabled: false,
      },
      isAuthenticated: true,
      requiresMfa: false,
      mfaToken: null,
    });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { data } = await axios.post(`${API_URL}/auth/login`, { email, password });

    if (data.requiresMfa) {
      setState(prev => ({
        ...prev,
        requiresMfa: true,
        mfaToken: data.mfaToken,
      }));
      return;
    }

    // Force password reset — redirect to reset page with token
    if (data.mustResetPassword) {
      const resetUrl = `/reset-password?email=${encodeURIComponent(email)}&token=${encodeURIComponent(data.passwordResetToken || '')}`;
      window.location.href = resetUrl;
      return;
    }

    // Store MFA flags for the banner — clear stale values when MFA is no longer suggested
    if (data.mfaSuggested === false) {
      sessionStorage.removeItem('mfaSuggested');
      sessionStorage.removeItem('mfaRequired');
    } else {
      sessionStorage.setItem('mfaSuggested', String(data.mfaSuggested || false));
      sessionStorage.setItem('mfaRequired', String(data.mfaRequired || false));
    }

    // Admin MFA required — tokens are issued but user must set up MFA.
    // Skip the forced /mfa-setup redirect on the OAuth authorize path so the
    // user lands back on the consent screen; they can set up MFA later.
    const onOAuthFlow = window.location.pathname.startsWith('/oauth/authorize');
    if (data.mfaRequired && data.accessToken && !onOAuthFlow) {
      applyTokenResponse(data);
      setTimeout(() => { window.location.href = '/mfa-setup'; }, 100);
      return;
    }

    applyTokenResponse(data);
    // Default: send the new session to home so they start on the role-appropriate
    // page. Exception: if they're partway through an OAuth authorize flow
    // (came in via Claude Desktop "Add custom connector"), keep the URL +
    // query string so OAuthAuthorizePage can pick the request back up.
    if (!onOAuthFlow && window.location.pathname !== '/') {
      window.history.replaceState(null, '', '/');
    }
  }, [applyTokenResponse]);

  const verifyMfa = useCallback(async (code: string) => {
    const { data } = await axios.post(`${API_URL}/auth/mfa/verify`, {
      mfaToken: state.mfaToken,
      code,
    });
    // MFA verified successfully — clear suggestion/required flags
    sessionStorage.removeItem('mfaSuggested');
    sessionStorage.removeItem('mfaRequired');
    applyTokenResponse(data);
  }, [state.mfaToken, applyTokenResponse]);

  const logout = useCallback(() => {
    setState({
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      requiresMfa: false,
      mfaToken: null,
    });
    sessionStorage.removeItem('auth');
    sessionStorage.removeItem('cadence_activePrincipal');
    // Navigate to root so next login starts fresh — *unless* we're partway
    // through an OAuth authorize flow, in which case wiping the URL kills the
    // pending request from the MCP client. Same exception that login() makes
    // when the session token expires mid-consent (which used to bounce the
    // user to / with no query string and an "Unknown client_id" error on the
    // empty re-mount).
    if (!window.location.pathname.startsWith('/oauth/authorize')) {
      window.history.replaceState(null, '', '/');
    }
  }, []);

  const ensureValidToken = useCallback(async (): Promise<string> => {
    if (state.accessToken && !isTokenExpired(state.accessToken)) {
      return state.accessToken;
    }

    if (!state.accessToken || !state.refreshToken) {
      logout();
      throw new Error('Session expired');
    }

    try {
      const { data } = await axios.post(`${API_URL}/auth/refresh`, {
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      });
      applyTokenResponse(data);
      return data.accessToken;
    } catch {
      logout();
      throw new Error('Session expired');
    }
  }, [state.accessToken, state.refreshToken, logout, applyTokenResponse]);

  return (
    <AuthContext.Provider value={{ ...state, login, verifyMfa, logout, ensureValidToken, applyTokens: applyTokenResponse }}>
      {children}
    </AuthContext.Provider>
  );
}
